import { TextField, TextFieldProps } from "@mui/material";
import { FormEvent, KeyboardEvent, WheelEvent, forwardRef, useMemo } from "react";

interface INumberInputRestProps {
	isContactNumber?: boolean;
	positiveOnly?: boolean;
	disableSigned?: boolean;
	disableDecimal?: boolean;
	minValue?: number;
	maxValue?: number;
}

type NumberInputPropsType = INumberInputRestProps & TextFieldProps;
type TextFieldSlotPropsType = TextFieldProps["slotProps"];

const NumberInput = forwardRef<HTMLInputElement, NumberInputPropsType>((props, ref) => {
	const { isContactNumber, positiveOnly, disableSigned, disableDecimal, minValue, maxValue, slotProps, ...restProps } =
		props;

	const numberInputSlotProps = useMemo<TextFieldSlotPropsType>(() => {
		const updatedSlotProps: TextFieldSlotPropsType = slotProps ? { ...slotProps } : {};

		if (positiveOnly) updatedSlotProps.htmlInput = { ...updatedSlotProps.htmlInput, min: 0 };
		if (typeof minValue === "number") updatedSlotProps.htmlInput = { ...updatedSlotProps.htmlInput, min: minValue };
		if (typeof maxValue === "number") updatedSlotProps.htmlInput = { ...updatedSlotProps.htmlInput, max: maxValue };

		return updatedSlotProps;
	}, [maxValue, minValue, positiveOnly, slotProps]);

	function handleWheelEvent(event: WheelEvent<HTMLDivElement>) {
		event.currentTarget.querySelector("input")?.blur();
	}

	function handleKeyDownEvent(event: KeyboardEvent<HTMLDivElement>) {
		if (
			event.key === "e" ||
			event.key === "E" ||
			(disableSigned && (event.key === "-" || event.key === "+")) ||
			(disableDecimal && event.key === ".")
		) {
			event.preventDefault();
		}
	}

	function handleInputEvent(event: FormEvent<HTMLDivElement>) {
		const InputElement = event.target as HTMLInputElement;

		if (isContactNumber) {
			InputElement.value = Math.max(0, parseInt(InputElement.value)).toString().slice(0, 10);
		}

		if (typeof minValue === "number") {
			const inputValue = parseInt(InputElement.value);
			if (inputValue < minValue) InputElement.value = minValue.toString();
		}

		if (typeof maxValue === "number") {
			const inputValue = parseInt(InputElement.value);
			if (inputValue > maxValue) InputElement.value = maxValue.toString();
		}
	}

	return (
		<TextField
			type="number"
			autoComplete="off"
			ref={ref}
			slotProps={numberInputSlotProps}
			onWheel={handleWheelEvent}
			onInput={handleInputEvent}
			onKeyDown={handleKeyDownEvent}
			{...restProps}
		/>
	);
});

export default NumberInput;
